import React, { useState, useEffect, useMemo } from "react";
import { API_BASE } from "../api/config";
import Layout from "../components/Layout";
import { useTable, useGlobalFilter, useSortBy, usePagination } from "react-table";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useAuth } from "../context/AuthContext";
import {
  FaTable,
  FaSearch,
  FaFileExcel,
  FaFileCsv,
  FaFilePdf,
  FaPrint,
  FaCalendarAlt,
  FaShareAlt,
  FaTimesCircle,
} from "react-icons/fa";

function Daysale() {
  const [sales, setSales] = useState([]);
  const [searchText, setSearchText] = useState("");
  const { loginType: rawLoginType, branch } = useAuth();
  const loginType = Number(rawLoginType || 0);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [companyName, setCompanyName] = useState("My Restaurant");

  // modal states
  const [showModal, setShowModal] = useState(false);
  const [selectedTxn, setSelectedTxn] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelMessage, setCancelMessage] = useState("");
  const [cancelRemark, setCancelRemark] = useState("");
  const [modalType, setModalType] = useState(""); // "cancel" or "share"

  useEffect(() => {
    const fetchCompany = async () => {
      try {
        const response = await fetch(
          `${API_BASE}?key=dzital_key&pswd=Hibujji@123&table=company&transac=get`
        );
        const result = await response.json();
        if (result.status === "ok" && result.data && result.data.length > 0) {
          setCompanyName(result.data[0].company_name);
        }
      } catch (err) {
        console.error("Error fetching company:", err);
      }
    };
    fetchCompany();
  }, []);

  const formatDateYMD = (date) => {
    const d = new Date(date);
    const month = `${d.getMonth() + 1}`.padStart(2, "0");
    const day = `${d.getDate()}`.padStart(2, "0");
    const year = d.getFullYear();
    return [year, month, day].join("-");
  };

  const formatDateDMY = (date) => {
    const d = new Date(date);
    const day = `${d.getDate()}`.padStart(2, "0");
    const month = `${d.getMonth() + 1}`.padStart(2, "0");
    const year = d.getFullYear();
    return [day, month, year].join("-");
  };

  const fetchSales = async (from = fromDate, to = toDate) => {
    if (!from || !to) return;
    setLoading(true);
    try {
      const url = `${API_BASE}?key=dzital_key&pswd=Hibujji@123&transac=get_billing_details_by_dates_all&from_date=${from}&to_date=${to}&condition=branch&branch=${branch}`;
      const response = await fetch(url);
      const data = await response.json();

      if (!Array.isArray(data)) {
        console.error("Invalid data received from API", data);
        setSales([]);
        setLoading(false);
        return;
      }

      const formattedData = data.map((item, index) => ({
        sno: index + 1,
        invoice_date: item.invoice_date ? formatDateDMY(item.invoice_date) : "",
        transaction_number: item.transaction_number || "",
        name: item.customer_name || "",
        mobile: item.customer_mobile || "",
        table: item.table_name || "",
        updated_by_name: item.updated_by_name || "",
        subtotal: parseFloat(item.subtotal) || 0,
        discount: parseFloat(item.invoice_disc_amt) || 0,
        voucher: parseFloat(item.coupon_discount) || 0,
        additional: parseFloat(item.invoice_additional_charges) || 0,
        invoice: parseFloat(item.invoice_value) || 0,
        payment_mode: item.payment_mode || "",
        cancel_status: Number(item.cancel_status) || 0,
        remark: item.remark || "",
      }));

      setSales(formattedData);
    } catch (error) {
      console.error("Error fetching sales data:", error);
      setSales([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    const today = formatDateYMD(new Date());
    setFromDate(today);
    setToDate(today);
    fetchSales(today, today);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredSales = useMemo(
    () =>
      sales.filter(
        (sale) =>
          sale.name.toLowerCase().includes(searchText.toLowerCase()) ||
          (sale.mobile || "").includes(searchText) ||
          (sale.table || "").toLowerCase().includes(searchText.toLowerCase())
      ),
    [sales, searchText]
  );

  // === Totals separated for cancelled and non-cancelled ===
  const { nonCancelledTotals, cancelledTotals } = useMemo(() => {
    const nonCancelled = {
      subtotal: 0,
      discount: 0,
      voucher: 0,
      additional: 0,
      invoice: 0,
    };
    const cancelled = {
      subtotal: 0,
      discount: 0,
      voucher: 0,
      additional: 0,
      invoice: 0,
    };

    filteredSales.forEach((curr) => {
      if (curr.cancel_status === 1) {
        cancelled.subtotal += curr.subtotal;
        cancelled.discount += curr.discount;
        cancelled.voucher += curr.voucher;
        cancelled.additional += curr.additional;
        cancelled.invoice += curr.invoice;
      } else {
        nonCancelled.subtotal += curr.subtotal;
        nonCancelled.discount += curr.discount;
        nonCancelled.voucher += curr.voucher;
        nonCancelled.additional += curr.additional;
        nonCancelled.invoice += curr.invoice;
      }
    });

    return { nonCancelledTotals: nonCancelled, cancelledTotals: cancelled };
  }, [filteredSales]);

  // ===== EXPORTS =====
  const exportExcel = () => {
    const dataForExport = filteredSales.map((sale) => ({
      SNo: sale.sno,
      Date: sale.invoice_date,
      Name: sale.name,
      Mobile: sale.mobile,
      Table: sale.table,
      Subtotal: sale.subtotal,
      Discount: sale.discount,
      Voucher: sale.voucher,
      "Additional Charges": sale.additional,
      "Invoice Value": sale.invoice,
      "Payment Mode": sale.payment_mode,
      Cancelled: sale.cancel_status === 1 ? "Yes" : "No",
    }));
    const ws = XLSX.utils.json_to_sheet(dataForExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Daysale");
    XLSX.writeFile(wb, "Daysale.xlsx");
  };

  const exportCSV = () => {
    const csvContent = [
      [
        "SNo",
        "Date",
        "Name",
        "Mobile",
        "Table",
        "Subtotal",
        "Discount",
        "Voucher",
        "Additional Charges",
        "Invoice",
        "Payment Mode",
        "Cancelled",
      ],
      ...filteredSales.map((sale) => [
        sale.sno,
        sale.invoice_date,
        sale.name,
        sale.mobile,
        sale.table,
        sale.subtotal,
        sale.discount,
        sale.voucher,
        sale.additional,
        sale.invoice,
        sale.payment_mode,
        sale.cancel_status === 1 ? "Yes" : "No",
      ]),
    ]
      .map((e) => e.join(","))
      .join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "Daysale.csv");
  };

  const exportPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(12);
    doc.text(companyName, 105, 15, { align: "center" });
    doc.text("Day Sale Report", 105, 22, { align: "center" });
    doc.text(`From: ${fromDate}  To: ${toDate}`, 105, 29, { align: "center" });

    const tableColumn = [
      "SNo",
      "Date",
      "Name",
      "Mobile",
      "Table",
      "Subtotal",
      "Discount",
      "Voucher",
      "Additional Charges",
      "Invoice",
      "Payment Mode",
      "Cancelled",
    ];
    const tableRows = filteredSales.map((sale) => [
      sale.sno,
      sale.invoice_date,
      sale.name,
      sale.mobile,
      sale.table,
      sale.subtotal,
      sale.discount,
      sale.voucher,
      sale.additional,
      sale.invoice,
      sale.payment_mode,
      sale.cancel_status === 1 ? "Yes" : "No",
    ]);

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 35,
    });

    doc.save("Daysale.pdf");
  };

  const handleShare = (transaction_number, mobile) => {
    if (!transaction_number) return;
    const shareUrl = `${window.location.origin}/bill-print/${transaction_number}`;
    if (mobile) {
      const waUrl = `https://wa.me/${mobile}?text=Here%20is%20your%20bill:%20${encodeURIComponent(
        shareUrl
      )}`;
      window.open(waUrl, "_blank");
    } else {
      navigator.clipboard.writeText(shareUrl);
      setCancelMessage("Bill link copied to clipboard!");
      setModalType("share");
      setShowModal(true);
    }
  };

  const openCancelModal = (transaction_number) => {
    setSelectedTxn(transaction_number);
    setCancelMessage("");
    setCancelRemark("");
    setModalType("cancel");
    setShowModal(true);
  };

  const confirmCancel = async () => {
    if (!selectedTxn) return;
    if (!cancelRemark.trim()) {
      setCancelMessage("Remark is required to cancel the bill.");
      return;
    }
    setCancelLoading(true);
    setCancelMessage("");
    try {
      const cancelUrl = `${API_BASE}?key=dzital_key&pswd=Hibujji@123&transac=set_cancel_bill&transaction_number=${selectedTxn}&remark=${encodeURIComponent(
        cancelRemark
      )}`;
      const res = await fetch(cancelUrl);
      const result = await res.json();
      if (result.status === "ok") {
        setCancelMessage("Bill cancelled successfully");
        fetchSales(fromDate, toDate);
      } else {
        setCancelMessage("Failed to cancel bill");
      }
    } catch (error) {
      console.error("Cancel error:", error);
      setCancelMessage("Error cancelling bill");
    }
    setCancelLoading(false);
  };

  const columns = useMemo(
    () => [
      { Header: "S.No", accessor: "sno" },
      { Header: "Date", accessor: "invoice_date" },
      { Header: "Name", accessor: "name" },
      { Header: "Mobile", accessor: "mobile" },
      {
        Header: "Table",
        accessor: "table",
        Cell: ({ row }) => (
          <div>
            <div>{row.original.table}</div>
            <div className="text-xs text-gray-300">
              By: {row.original.updated_by_name}
            </div>
          </div>
        ),
      },
      { Header: "Subtotal", accessor: "subtotal" },
      { Header: "Discount", accessor: "discount" },
      { Header: "Voucher", accessor: "voucher" },
      { Header: "Additional Charges", accessor: "additional" },
      { Header: "Invoice Value", accessor: "invoice" },
      { Header: "Payment Mode", accessor: "payment_mode" },
      {
        Header: "Remark",
        accessor: "remark",
        Cell: ({ row }) =>
          row.original.cancel_status === 1
            ? `Bill Cancelled - ${row.original.remark}`
            : "",
      },
      {
        Header: "Actions",
        Cell: ({ row }) => (
          <div className="flex flex-wrap gap-1">
            <button
              onClick={() =>
                window.open(`/bill-print/${row.original.transaction_number}`, "_blank")
              }
              className="bg-gray-800 text-white px-2 py-1 rounded flex items-center gap-1 text-sm"
            >
              <FaPrint /> Print
            </button>
            <button
              onClick={() =>
                handleShare(row.original.transaction_number, row.original.mobile)
              }
              className="bg-blue-600 text-white px-2 py-1 rounded flex items-center gap-1 text-sm"
            >
              <FaShareAlt /> Share
            </button>
            {(loginType === 1 || loginType === 2) &&
              row.original.cancel_status !== 1 && (
                <button
                  onClick={() => openCancelModal(row.original.transaction_number)}
                  className="bg-red-600 text-white px-2 py-1 rounded flex items-center gap-1 text-sm"
                >
                  <FaTimesCircle /> Cancel
                </button>
              )}
          </div>
        ),
      },
    ],
    [loginType]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    pageOptions,
    state: { pageIndex },
    setGlobalFilter,
  } = useTable(
    { columns, data: filteredSales, initialState: { pageIndex: 0, pageSize: 10 } },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  useEffect(() => {
    setGlobalFilter(searchText || undefined);
  }, [searchText, setGlobalFilter]);

  return (
    <Layout>
      <div className="p-4 bg-gradient-to-b from-blue-900 to-black min-h-screen text-white text-sm">
        <h2 className="text-xl font-bold mb-3 flex items-center">
          <FaTable className="mr-2" /> Day Sale Report
        </h2>

        {/* Filters */}
        <div className="flex flex-wrap mb-3 items-center gap-2 text-sm">
          <div className="flex items-center gap-1">
            <FaCalendarAlt />
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="border rounded p-1 text-black"
            />
          </div>
          <div className="flex items-center gap-1">
            <FaCalendarAlt />
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="border rounded p-1 text-black"
            />
          </div>
          <button
            onClick={() => fetchSales(fromDate, toDate)}
            className="bg-green-600 text-white px-3 py-1 rounded"
          >
            Fetch
          </button>
          <div className="flex items-center gap-1">
            <FaSearch />
            <input
              type="text"
              placeholder="Search..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="border rounded p-1 text-black"
            />
          </div>
          <button onClick={exportExcel} className="bg-green-600 px-3 py-1 rounded">
            <FaFileExcel />
          </button>
          <button onClick={exportCSV} className="bg-blue-600 px-3 py-1 rounded">
            <FaFileCsv />
          </button>
          <button onClick={exportPDF} className="bg-red-600 px-3 py-1 rounded">
            <FaFilePdf />
          </button>
        </div>

        {/* Table */}
        <div className="overflow-x-auto bg-gray-800 rounded-lg shadow">
          <table {...getTableProps()} className="min-w-full">
            <thead className="bg-blue-700 text-white">
              {headerGroups.map((hg) => (
                <tr {...hg.getHeaderGroupProps()}>
                  {hg.headers.map((col) => (
                    <th {...col.getHeaderProps(col.getSortByToggleProps())} className="p-2">
                      {col.render("Header")}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {page.map((row) => {
                prepareRow(row);
                return (
                  <tr
                    {...row.getRowProps()}
                    className={`border-b border-gray-700 ${
                      row.original.cancel_status === 1
                        ? "bg-red-900 text-gray-300"
                        : "hover:bg-gray-700"
                    }`}
                  >
                    {row.cells.map((cell) => (
                      <td {...cell.getCellProps()} className="p-2 text-center">
                        {cell.render("Cell")}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex justify-between items-center mt-3 text-sm">
          <div>
            Page {pageIndex + 1} of {pageOptions.length}
          </div>
          <div className="flex gap-2">
            <button
              onClick={previousPage}
              disabled={!canPreviousPage}
              className="bg-gray-700 px-2 py-1 rounded disabled:opacity-40"
            >
              Prev
            </button>
            <button
              onClick={nextPage}
              disabled={!canNextPage}
              className="bg-gray-700 px-2 py-1 rounded disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>

        {/* Totals */}
        <div className="mt-5 grid grid-cols-2 gap-4 text-sm">
          <div className="bg-green-900 p-3 rounded">
            <h3 className="font-bold mb-2 text-green-300">Non-Cancelled Totals</h3>
            <p>Subtotal: ₹{nonCancelledTotals.subtotal.toFixed(2)}</p>
            <p>Discount: ₹{nonCancelledTotals.discount.toFixed(2)}</p>
            <p>Voucher: ₹{nonCancelledTotals.voucher.toFixed(2)}</p>
            <p>Additional: ₹{nonCancelledTotals.additional.toFixed(2)}</p>
            <p>Invoice Value: ₹{nonCancelledTotals.invoice.toFixed(2)}</p>
          </div>
          <div className="bg-red-900 p-3 rounded">
            <h3 className="font-bold mb-2 text-red-300">Cancelled Totals</h3>
            <p>Subtotal: ₹{cancelledTotals.subtotal.toFixed(2)}</p>
            <p>Discount: ₹{cancelledTotals.discount.toFixed(2)}</p>
            <p>Voucher: ₹{cancelledTotals.voucher.toFixed(2)}</p>
            <p>Additional: ₹{cancelledTotals.additional.toFixed(2)}</p>
            <p>Invoice Value: ₹{cancelledTotals.invoice.toFixed(2)}</p>
          </div>
        </div>

        {/* Cancel/Share Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
            <div className="bg-gray-900 p-5 rounded shadow-lg w-96 text-white">
              {modalType === "cancel" && (
                <>
                  <h3 className="text-lg font-bold mb-2">Cancel Bill</h3>
                  <textarea
                    placeholder="Enter cancel remark"
                    value={cancelRemark}
                    onChange={(e) => setCancelRemark(e.target.value)}
                    className="w-full p-2 rounded text-black mb-2"
                  />
                  <button
                    onClick={confirmCancel}
                    disabled={cancelLoading}
                    className="bg-red-600 px-3 py-1 rounded mr-2"
                  >
                    {cancelLoading ? "Cancelling..." : "Confirm"}
                  </button>
                </>
              )}

              {modalType === "share" && (
                <h3 className="text-green-400 text-lg">Bill link copied!</h3>
              )}

              {cancelMessage && <p className="mt-2 text-yellow-300">{cancelMessage}</p>}

              <button
                onClick={() => setShowModal(false)}
                className="bg-gray-700 px-3 py-1 rounded mt-3"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default Daysale;
